import { MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { FastspringSubscriptionName } from '../../common/enums.ts';
import queryHooks from '../../common/queryHooks/index.ts';
import mutationHooks from '../../common/mutationHooks/index.ts';
import useCurrentCustomerLocationId from '../../hooks/useCurrentCustomerLocation.ts';

const validSubscriptions = [
  FastspringSubscriptionName.medtraxBasicService,
  FastspringSubscriptionName.medtraxBasicServiceAnnual,
  FastspringSubscriptionName.medtraxEnterpriseService,
  FastspringSubscriptionName.medtraxEnterpriseServiceAnnual,
  FastspringSubscriptionName.medtraxEssentialsService,
  FastspringSubscriptionName.medtraxEssentialsServiceAnnual,
  FastspringSubscriptionName.medtraxMMCAPService,
  FastspringSubscriptionName.medtraxPlusService,
  FastspringSubscriptionName.medtraxPlusServiceAnnual,
  FastspringSubscriptionName.medtraxPremierService,
];

const ManageLocations = () => {
  const navigate = useNavigate();
  const { customerLocationId } = useCurrentCustomerLocationId();

  const { data: customerLocations } =
    queryHooks.customerLocation.useReadAllHydratedByUserId();

  const { data: isAdmin } = queryHooks.user.useGetIsAdminById(
    { customerLocationId: Number(customerLocationId) },
    { refetchOnMount: true }
  );
  const { data: subscriptions } =
    queryHooks.fastspring.useReadAllSubscriptionsById();
  const { mutate: reactivateLocation } =
    mutationHooks.customerLocations.useReactivateCustomerLocations();

  const totalSubscriptions = useMemo(() => {
    if (!subscriptions) return 0;
    return subscriptions
      ?.filter(
        sub =>
          sub.active &&
          validSubscriptions.includes(sub.product as FastspringSubscriptionName)
      )
      .reduce((acc, sub) => acc + sub.quantity, 0);
  }, [subscriptions]);

  const activeLocations = useMemo(() => {
    return customerLocations?.filter(location => location.isActive) ?? [];
  }, [customerLocations]);

  const numUnusedLocations =
    totalSubscriptions - (activeLocations?.length ?? 0);
  const goToLocation = (id: number) => {
    navigate(`/locations/${id}`);
  };

  const goToAddLocation = (event: MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();
    navigate('/locations/add');
  };

  return (
    <div>
      <div className="row">
        <h5>Locations</h5>
      </div>
      {numUnusedLocations && numUnusedLocations > 0 ? (
        <div className="row">
          <div className="col s12 m4 offset-m8">
            <a
              href="/"
              className="btn-small blue white-text wave-effect waves-light col s12"
              onClick={goToAddLocation}
            >
              Add Location
            </a>
          </div>
        </div>
      ) : null}
      <div className="row">
        <span>Total Subscriptions: {totalSubscriptions || 0}</span>
        <br />
        <span>Unused Subscriptions: {numUnusedLocations || 0}</span>
      </div>
      <div className="row">
        <table className="highlight">
          <thead>
            <tr>
              <th>Name</th>
              <th>City, State</th>
              <th></th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {customerLocations?.map(
              ({ id, name, customerLocationAddresses: [addr], isActive }) => (
                <tr>
                  <td
                    style={{ padding: '2px', cursor: 'pointer' }}
                    key={id}
                    onClick={() => goToLocation(id)}
                  >
                    {name}
                  </td>
                  <td style={{ padding: '2px' }}>{`${addr?.city || ''}${
                    addr?.city && addr?.state ? ', ' : ''
                  }${addr?.state.code || ''}`}</td>
                  <td>
                    {isAdmin?.isAdmin &&
                      numUnusedLocations > 0 &&
                      !isActive && (
                        <button onClick={() => reactivateLocation(id)}>
                          reactivate
                        </button>
                      )}
                  </td>
                  <td
                    style={{
                      padding: '2px',
                      color: isActive ? 'black' : 'red',
                    }}
                  >
                    {isActive ? 'yes' : 'no'}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageLocations;
